import PropTypes from 'prop-types';
import React from 'react';

import { Header, Footer, SideLine, Background } from '$partials/global/index.js';
import { Image } from '$partials/media';
import { ModuleDispatcher } from '$partials/modules';
import { getBackgroundColours } from '$utilities/site-context';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ContentPage = (props) => {
    const page = props.data.page;

    return (
        <>
            <Background {...getBackgroundColours()} />

            {/* Navigation */}
            <Header navigation={props.data.navigation} />

            {/* Main content */}
            <main
                className='pt-40 sm:pt-50 md:pt-100 min-h-screen relative'
                id='main-content'>
                <SideLine/>

                <div className='layout-container'>
                    <div className='flex flex-wrap layout-negative-margin'>
                        {/* Standfirst */}
                        <div className='sm:w-9/12 layout-item-padding'>
                            <h1 className='type-style-3'>
                                {page?.standfirst}
                            </h1>
                        </div>

                    </div>
                </div>

                {/* Spacer */}
                <div className='mt-60 md:mt-100'/>


                <div className={`layout-container-padding-right flex flex-wrap max-w-container bg-white pt-60 sm:pt-70 md:pt-80 ${(page.modules.length > 0) && page.modules[0].__typename === 'Text' ? 'rounded-t-md' : 'rounded-t-md pb-80'}`}>
                    {/* Left Sidebar */}
                    <div className='w-sidebar'>
                        <div className='flex flex-col items-center'>
                            <h2 className='type-style-8 inline-block align-middle rotate-180 writing-mode-vertical-rl'>
                                {page?.sectionSidebarTitle}
                            </h2>
                        </div>
                    </div>

                    {/* Main content */}
                    <div className='flex-1 flex flex-wrap self-start layout-negative-margin'>
                        {/* First section */}
                        {/* Keywords */}
                        <section className={`${page?.keywords?.length > 0 ? 'sm:flex flex-row-reverse' : ''}`}>
                            {page?.keywords?.length > 0 &&
                            <div className='sm:w-4/12 mb-70 sm:mb-0 layout-item-padding'>
                                <h3 className='type-style-5 pb-20'>
                                    {page?.keywordsHeading}
                                </h3>
                                <div className='grid grid-cols-2 sm:max-md:grid-cols-1 layout-gap gap-y-30 md:gap-y-55'>
                                    {page?.keywords.map(function(item, i) {
                                        return (
                                            <div
                                                className='flex phoenix_court-border background-4px pl-35'
                                                key={`keyword-item-${i}`}>

                                                <div className='inline-block'>
                                                    <p className='type-style-5 block mb-10'>
                                                        {item.heading}
                                                    </p>
                                                    <p className='type-style-8 block'>
                                                        {item.text}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            }
                            {/* spacer */}
                            <div className='sm:w-1/12'/>

                            <div className='sm:w-7/12 layout-item-padding'>
                                <div className='md:w-6/7'>
                                    <h3 className='type-style-4 pb-10'>
                                        {page?.sectionHeading}
                                    </h3>
                                    <div dangerouslySetInnerHTML={{__html: page?.sectionText }}/>
                                </div>
                                {page?.sectionImage &&
                                    <Image
                                        containerClassName='rounded-lg mt-30 md:mt-40'
                                        {...page.sectionImage} />
                                }
                            </div>
                        </section>
                    </div>
                </div>

                {/* Module dispatcher */}
                {page.modules?.length > 0 &&
                    <section className='content-modules'>
                        {page.modules.map((module, i, { length, ...modules }) => {
                            const isLast = length - 1 === i;
                            const nextModuleType = modules[i + 1]?.__typename;
                            const previousModuleType = modules[i - 1]?.__typename;
                            return (
                                <ModuleDispatcher
                                    {...module}
                                    isLast={isLast}
                                    key={i + module?.id}
                                    nextModuleType={nextModuleType}
                                    pageTypename={page.__typename}
                                    previousModuleType={previousModuleType}/>
                            );
                        })}
                    </section>
                }
            </main>

            {/* Footer */}
            {props.data?.footer && (
                <Footer {...props.data.footer} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            __typename: PropTypes.string,
            keywords: PropTypes.arrayOf(PropTypes.shape({
                heading: PropTypes.string,
                text: PropTypes.string,
            })),
            keywordsHeading: PropTypes.string,
            modules: PropTypes.array,
            sectionHeading: PropTypes.string,
            sectionImage: PropTypes.object,
            sectionSidebarTitle: PropTypes.string,
            sectionText: PropTypes.string,
            standfirst: PropTypes.string,
        }),
        navigation: PropTypes.object,
        footer: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ContentPage.propTypes = propTypes;

export default ContentPage;
